import { Plus, Trash, Percent, Confetti, Package } from "@phosphor-icons/react";

const DEFAULT_WEEKEND = { active: false, type: "DISCOUNT_PERCENT", discount_pct: "", pickup_from: "12:00", return_until: "12:00" };

// Editor reutilizável de regras de preço (descontos progressivos + promo fim de semana + desconto por quantidade).
// value = { discount_rules: [{min_duration, discount_pct}], weekend_promo: {...}, quantity_discount_rules: [{min_quantity, discount_pct}] }
export function PricingRulesEditor({ value, onChange, unitLabel = "dias", showWeekend = true }) {
  const rules = value?.discount_rules || [];
  const weekend = value?.weekend_promo || DEFAULT_WEEKEND;
  const qrules = value?.quantity_discount_rules || [];

  const setRules = (next) => onChange({ discount_rules: next, weekend_promo: weekend, quantity_discount_rules: qrules });
  const setWeekend = (next) => onChange({ discount_rules: rules, weekend_promo: next, quantity_discount_rules: qrules });
  const setQRules = (next) => onChange({ discount_rules: rules, weekend_promo: weekend, quantity_discount_rules: next });

  const addRule = () => {
    if (rules.length >= 3) return;
    setRules([...rules, { min_duration: "", discount_pct: "" }]);
  };
  const updRule = (i, key, v) => setRules(rules.map((r, idx) => (idx === i ? { ...r, [key]: v } : r)));
  const delRule = (i) => setRules(rules.filter((_, idx) => idx !== i));

  const addQRule = () => {
    if (qrules.length >= 3) return;
    setQRules([...qrules, { min_quantity: "", discount_pct: "" }]);
  };
  const updQRule = (i, key, v) => setQRules(qrules.map((r, idx) => (idx === i ? { ...r, [key]: v } : r)));
  const delQRule = (i) => setQRules(qrules.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4" data-testid="pricing-rules-editor">
      {/* Descontos progressivos */}
      <div className="aj-card p-3 border border-slate-200">
        <div className="flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wider text-slate-600 mb-1">
          <Percent size={14} weight="bold" className="text-[#004E98]" /> Descontos progressivos (até 3)
        </div>
        <p className="text-[11px] text-slate-400 mb-2">Quanto mais tempo o cliente alugar, maior o desconto. Ex.: acima de 7 {unitLabel}, 10% off.</p>
        <div className="space-y-2">
          {rules.map((r, i) => (
            <div key={i} className="flex items-center gap-1.5 text-sm" data-testid={`discount-rule-${i}`}>
              <span className="text-slate-500 text-xs whitespace-nowrap">Acima de</span>
              <input
                type="number" min="1" step="1" inputMode="numeric" placeholder="7"
                value={r.min_duration} onChange={(e) => updRule(i, "min_duration", e.target.value)}
                className="aj-input w-16 text-center" data-testid={`discount-min-${i}`} />
              <span className="text-slate-500 text-xs whitespace-nowrap">{unitLabel} →</span>
              <input
                type="number" min="1" max="90" step="1" inputMode="numeric" placeholder="10"
                value={r.discount_pct} onChange={(e) => updRule(i, "discount_pct", e.target.value)}
                className="aj-input w-16 text-center" data-testid={`discount-pct-${i}`} />
              <span className="text-slate-500 text-xs">% off</span>
              <button type="button" onClick={() => delRule(i)} className="ml-auto p-1.5 rounded-lg text-red-500 hover:bg-red-50" data-testid={`discount-del-${i}`} aria-label="Remover regra">
                <Trash size={16} weight="bold" />
              </button>
            </div>
          ))}
        </div>
        {rules.length < 3 && (
          <button type="button" onClick={addRule} className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-[#004E98]" data-testid="add-discount-rule">
            <Plus size={14} weight="bold" /> Adicionar regra
          </button>
        )}
      </div>

      {/* V18.9 — Descontos por quantidade de itens */}
      <div className="aj-card p-3 border border-slate-200">
        <div className="flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wider text-slate-600 mb-1">
          <Package size={14} weight="bold" className="text-[#004E98]" /> Desconto por quantidade (até 3)
        </div>
        <p className="text-[11px] text-slate-400 mb-2">Ao alugar em maior volume, o cliente ganha desconto. Ex.: a partir de 5 itens, 10% off.</p>
        <div className="space-y-2">
          {qrules.map((r, i) => (
            <div key={i} className="flex items-center gap-1.5 text-sm" data-testid={`qty-discount-rule-${i}`}>
              <span className="text-slate-500 text-xs whitespace-nowrap">A partir de</span>
              <input
                type="number" min="2" step="1" inputMode="numeric" placeholder="5"
                value={r.min_quantity} onChange={(e) => updQRule(i, "min_quantity", e.target.value)}
                className="aj-input w-16 text-center" data-testid={`qty-discount-min-${i}`} />
              <span className="text-slate-500 text-xs whitespace-nowrap">itens →</span>
              <input
                type="number" min="1" max="90" step="1" inputMode="numeric" placeholder="10"
                value={r.discount_pct} onChange={(e) => updQRule(i, "discount_pct", e.target.value)}
                className="aj-input w-16 text-center" data-testid={`qty-discount-pct-${i}`} />
              <span className="text-slate-500 text-xs">% off</span>
              <button type="button" onClick={() => delQRule(i)} className="ml-auto p-1.5 rounded-lg text-red-500 hover:bg-red-50" data-testid={`qty-discount-del-${i}`} aria-label="Remover regra">
                <Trash size={16} weight="bold" />
              </button>
            </div>
          ))}
        </div>
        {qrules.length < 3 && (
          <button type="button" onClick={addQRule} className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-[#004E98]" data-testid="add-qty-discount-rule">
            <Plus size={14} weight="bold" /> Adicionar regra
          </button>
        )}
      </div>

      {/* Promoção de fim de semana (apenas locação diária) */}
      {showWeekend && (
      <div className="aj-card p-3 border border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wider text-slate-600">
            <Confetti size={14} weight="bold" className="text-[#FF6B35]" /> Promoção de Fim de Semana
          </div>
          <button
            type="button" role="switch" aria-checked={weekend.active}
            onClick={() => setWeekend({ ...weekend, active: !weekend.active })}
            className={`relative w-11 h-6 rounded-full transition-colors ${weekend.active ? "bg-[#16A34A]" : "bg-slate-300"}`}
            data-testid="weekend-promo-toggle">
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${weekend.active ? "translate-x-5" : ""}`} />
          </button>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">Desconta a parte do fim de semana de qualquer aluguel. Sábado e domingo sempre contam; a sexta entra a partir do horário definido.</p>
        {weekend.active && (
          <div className="space-y-2 mt-3" data-testid="weekend-promo-options">
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] uppercase font-bold text-slate-500">Sexta a partir de</span>
                <input type="time" value={weekend.pickup_from || "12:00"} onChange={(e) => setWeekend({ ...weekend, pickup_from: e.target.value })} className="aj-input text-sm" data-testid="weekend-pickup-from" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase font-bold text-slate-500">Devolução segunda até</span>
                <input type="time" value={weekend.return_until || "12:00"} onChange={(e) => setWeekend({ ...weekend, return_until: e.target.value })} className="aj-input text-sm" data-testid="weekend-return-until" />
              </label>
            </div>
            <button
              type="button" onClick={() => setWeekend({ ...weekend, type: "DISCOUNT_PERCENT" })}
              className={`w-full text-left p-2.5 rounded-xl border-2 ${weekend.type === "DISCOUNT_PERCENT" ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
              data-testid="weekend-type-percent">
              <div className="font-bold text-sm">Desconto % no fim de semana</div>
              <div className="text-[11px] text-slate-500">Aplica um % de desconto nas diárias de sábado/domingo (e sexta, se a partir do horário).</div>
              {weekend.type === "DISCOUNT_PERCENT" && (
                <div className="flex items-center gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="number" min="1" max="90" step="1" inputMode="numeric" placeholder="15"
                    value={weekend.discount_pct} onChange={(e) => setWeekend({ ...weekend, discount_pct: e.target.value })}
                    className="aj-input w-20 text-center" data-testid="weekend-pct-input" />
                  <span className="text-sm text-slate-500">% de desconto</span>
                </div>
              )}
            </button>
            <button
              type="button" onClick={() => setWeekend({ ...weekend, type: "FLAT_ONE_DAY" })}
              className={`w-full text-left p-2.5 rounded-xl border-2 ${weekend.type === "FLAT_ONE_DAY" ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-slate-200"}`}
              data-testid="weekend-type-flat">
              <div className="font-bold text-sm">Fim de semana pelo preço de 1 diária ⚡</div>
              <div className="text-[11px] text-slate-500">As diárias do fim de semana são cobradas como 1 diária só (em vez de 2).</div>
            </button>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// Sanitiza as regras para envio à API (números válidos; omite incompletas).
export function sanitizePricingRules({ discount_rules, weekend_promo, quantity_discount_rules }) {
  const rules = (discount_rules || [])
    .map((r) => ({ min_duration: parseInt(r.min_duration, 10), discount_pct: parseFloat(r.discount_pct) }))
    .filter((r) => Number.isFinite(r.min_duration) && r.min_duration >= 1 && Number.isFinite(r.discount_pct) && r.discount_pct > 0 && r.discount_pct <= 90);
  const wp = weekend_promo || DEFAULT_WEEKEND;
  const weekend = wp.active
    ? {
        active: true,
        type: wp.type || "DISCOUNT_PERCENT",
        discount_pct: wp.type === "FLAT_ONE_DAY" ? null : (parseFloat(wp.discount_pct) || 0),
        pickup_from: wp.pickup_from || "12:00",
        return_until: wp.return_until || "12:00",
      }
    : { active: false, type: null, discount_pct: null, pickup_from: null, return_until: null };
  const qrules = (quantity_discount_rules || [])
    .map((r) => ({ min_quantity: parseInt(r.min_quantity, 10), discount_pct: parseFloat(r.discount_pct) }))
    .filter((r) => Number.isFinite(r.min_quantity) && r.min_quantity >= 2 && Number.isFinite(r.discount_pct) && r.discount_pct > 0 && r.discount_pct <= 90);
  return { discount_rules: rules, weekend_promo: weekend, quantity_discount_rules: qrules };
}

export { DEFAULT_WEEKEND };
